/** Dialog for managing supplier gateways. */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Router as GatewayIcon,
} from "@mui/icons-material";
import { SupplierGatewaySummary, SupplierGatewayCreate, SupplierStatus } from "../../types";
import ConfirmDialog from "../common/ConfirmDialog";

interface GatewayManageDialogProps {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  supplierName: string;
  gateways: SupplierGatewaySummary[];
  onCreateGateway: (data: SupplierGatewayCreate) => Promise<void>;
  onUpdateGateway: (gatewayId: number, data: SupplierGatewayCreate) => Promise<void>;
  onDeleteGateway: (gatewayId: number) => Promise<void>;
  supplierStatus: SupplierStatus;
}

/** Gateway form data interface. */
interface GatewayFormData {
  hostname?: string;
  ip_address: string;
  port: string;
  auth_type: string;
  username: string;
  password: string;
  load_share: string;
  priority: string;
  capacity_cps: string;
  capacity_cc: string;
  enabled: boolean;
  inbound_codecs: string;
  outbound_codecs: string;
  strip_digits: string;
  prepend_prefix: string;
  ani_rewrite: string;
}

/** CIDR validation regex. */
const CIDR_REGEX = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

/** Validate CIDR notation. */
const validateCIDR = (cidr: string): boolean => {
  if (!CIDR_REGEX.test(cidr)) return false;

  const [ip, prefix] = cidr.split("/");
  const prefixNum = parseInt(prefix, 10);

  if (prefixNum < 0 || prefixNum > 32) return false;

  const octets = ip.split(".");
  for (const octet of octets) {
    const num = parseInt(octet, 10);
    if (num < 0 || num > 255) return false;
  }

  return true;
};

/** Gateway management dialog with add/edit functionality. */
export default function GatewayManageDialog({
  open,
  onClose,
  supplierId,
  supplierName,
  gateways,
  onCreateGateway,
  onUpdateGateway,
  onDeleteGateway,
  supplierStatus,
}: GatewayManageDialogProps) {
  const [formMode, setFormMode] = useState<"list" | "add" | "edit">("list");
  const [editingGateway, setEditingGateway] = useState<SupplierGatewaySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [formData, setFormData] = useState<GatewayFormData>({
    ip_address: "",
    port: "5060",
    auth_type: "ip",
    username: "",
    password: "",
    load_share: "100",
    priority: "1",
    capacity_cps: "100",
    strip_digits: "0",
    prepend_prefix: "",
    ani_rewrite: "",
    capacity_cc: "0",
    enabled: true,
    inbound_codecs: "",
    outbound_codecs: "",
  });

  const [ipError, setIpError] = useState("");

  /** Reset form when opening/closing. */
  useEffect(() => {
    if (open) {
      setFormMode("list");
      setEditingGateway(null);
      setError("");
      resetForm();
    }
  }, [open]);

  /** Reset form to default values. */
  const resetForm = () => {
    setFormData({
      ip_address: "",
      port: "5060",
      auth_type: "ip",
      username: "",
      password: "",
      load_share: "100",
      priority: "1",
      capacity_cps: "100",
    strip_digits: "0",
    prepend_prefix: "",
    ani_rewrite: "",
      capacity_cc: "0",
      enabled: true,
      inbound_codecs: "",
      outbound_codecs: "",
    });
    setIpError("");
  };

  /** Load gateway data into form for editing. */
  const handleEditClick = (gateway: SupplierGatewaySummary) => {
    setEditingGateway(gateway as any); // Store for reference
    // DEFENSE AT THE TYPE BOUNDARY: this handler must tolerate summary-shaped
    // objects (list endpoints may strip fields) — an undefined read here
    // crashes the render and the user loses the whole form.
    setFormData({
      hostname: gateway.hostname || "",
      ip_address: gateway.ip_address || "",
      port: String(gateway.port ?? 5060),
      auth_type: gateway.auth_type || "ip",
      username: gateway.username || "",
      password: gateway.password || "",
      load_share: String(gateway.load_share ?? 100),
      priority: String(gateway.priority ?? 1),
      capacity_cps: String(gateway.capacity_cps ?? 100),
      capacity_cc: String(gateway.capacity_cc ?? 0),
      inbound_codecs: gateway.inbound_codecs ?? "",
      outbound_codecs: gateway.outbound_codecs ?? "",
      enabled: gateway.enabled ?? true,
      strip_digits: String(gateway.strip_digits ?? 0),
      prepend_prefix: gateway.prepend_prefix ?? "",
      ani_rewrite: gateway.ani_rewrite ?? "",
    });
    setFormMode("edit");
    setError("");
  };

  /** Handle adding a new gateway. */
  const handleAddClick = () => {
    resetForm();
    setFormMode("add");
    setError("");
  };

  /** Handle form submission. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate IP/CIDR
    if (!validateCIDR(formData.ip_address)) {
      setIpError("Invalid CIDR format (e.g., 192.168.1.0/24)");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const gatewayData: SupplierGatewayCreate = {
        ip_address: formData.ip_address,
        port: parseInt(formData.port, 10),
        auth_type: formData.auth_type,
        username: formData.username || undefined,
        password: formData.password || undefined,
        load_share: parseInt(formData.load_share, 10),
        priority: parseInt(formData.priority, 10),
        capacity_cps: parseInt(formData.capacity_cps, 10),
        capacity_cc: parseInt(formData.capacity_cc, 10),
        enabled: formData.enabled,
        inbound_codecs: formData.inbound_codecs.trim() || undefined,
        outbound_codecs: formData.outbound_codecs.trim() || undefined,
        // RT-4: number translation / ANI (form-drift fix: these were collected
        // in formData but never sent — edits silently did nothing)
        strip_digits: parseInt(formData.strip_digits, 10) || 0,
        prepend_prefix: formData.prepend_prefix || "",
        ani_rewrite: formData.ani_rewrite || undefined,
      };

      if (formData.hostname) {
        gatewayData.hostname = formData.hostname;
      }

      if (formMode === "add") {
        await onCreateGateway(gatewayData);
      } else if (editingGateway) {
        await onUpdateGateway(editingGateway.id, gatewayData);
      }

      // Return to list view
      setFormMode("list");
      setEditingGateway(null);
      resetForm();
    } catch (err: any) {
      console.error("Failed to save gateway:", err);
      setError(
        err.response?.data?.detail || "Failed to save gateway. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Handle canceling form mode. */
  const handleCancelForm = () => {
    setFormMode("list");
    setEditingGateway(null);
    resetForm();
    setError("");
  };

  /** Handle deleting a gateway — confirmed via styled dialog (UX review C2). */
  const [pendingDelete, setPendingDelete] = useState<SupplierGatewaySummary | null>(null);

  const handleDeleteGatewayRequest = (gateway: SupplierGatewaySummary) => {
    setPendingDelete(gateway);
  };

  const handleDeleteGateway = async () => {
    if (!pendingDelete) return;

    setLoading(true);
    setError("");

    try {
      await onDeleteGateway(pendingDelete.id);
      setPendingDelete(null);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || "Failed to delete gateway. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Render form for add/edit. */
  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          label="IP Address / CIDR"
          fullWidth
          variant="outlined"
          value={formData.ip_address}
          onChange={(e) => {
            setFormData({ ...formData, ip_address: e.target.value });
            if (e.target.value && !validateCIDR(e.target.value)) {
              setIpError("Invalid CIDR format (e.g., 192.168.1.0/24)");
            } else {
              setIpError("");
            }
          }}
          error={!!ipError}
          helperText={ipError || "e.g., 192.168.1.100/32 or 10.0.0.0/24"}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            margin="dense"
            label="Port"
            type="number"
            variant="outlined"
            value={formData.port}
            onChange={(e) => setFormData({ ...formData, port: e.target.value })}
            disabled={loading}
            inputProps={{ min: 1, max: 65535 }}
          />
          <TextField
            margin="dense"
            label="Hostname (optional)"
            variant="outlined"
            value={formData.hostname}
            onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
            disabled={loading}
            helperText="Override hostname for this gateway"
          />
        </Box>

        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
          <InputLabel>Auth Type</InputLabel>
          <Select
            value={formData.auth_type}
            label="Auth Type"
            onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
            disabled={loading}
          >
            <MenuItem value="ip">IP Whitelist</MenuItem>
            <MenuItem value="userpass">Username/Password</MenuItem>
          </Select>
        </FormControl>

        {formData.auth_type === "userpass" && (
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Username"
              variant="outlined"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={loading}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Password"
              variant="outlined"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              disabled={loading}
              fullWidth
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            margin="dense"
            label="Load Share"
            type="number"
            variant="outlined"
            value={formData.load_share}
            onChange={(e) => setFormData({ ...formData, load_share: e.target.value })}
            disabled={loading}
            helperText="Relative traffic weight (1-1000)"
            inputProps={{ min: 1, max: 1000 }}
          />
          <TextField
            margin="dense"
            label="Priority"
            type="number"
            variant="outlined"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            disabled={loading}
            helperText="Lower = higher priority"
            inputProps={{ min: 1, max: 100 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            margin="dense"
            label="Max CPS"
            type="number"
            variant="outlined"
            value={formData.capacity_cps}
            onChange={(e) => setFormData({ ...formData, capacity_cps: e.target.value })}
            disabled={loading}
            inputProps={{ min: 0 }}
          />
          <TextField
            margin="dense"
            label="Max CC (0 = unlimited)"
            type="number"
            variant="outlined"
            value={formData.capacity_cc}
            onChange={(e) => setFormData({ ...formData, capacity_cc: e.target.value })}
            disabled={loading}
            inputProps={{ min: 0 }}
            helperText="Concurrent-call cap enforced by the billing engine; 0 = unlimited"
          />
        </Box>

        {/* Wave 6.2: per-gateway codec negotiation */}
        <TextField
          fullWidth
          margin="dense"
          label="Inbound codecs (optional)"
          value={formData.inbound_codecs}
          onChange={(e) => setFormData({ ...formData, inbound_codecs: e.target.value })}
          disabled={loading}
          placeholder="PCMA,PCMU,G729"
          helperText="Comma-separated; empty = profile policy (passthrough-first)"
        />
        <TextField
          fullWidth
          margin="dense"
          label="Outbound codec order (optional)"
          value={formData.outbound_codecs}
          onChange={(e) => setFormData({ ...formData, outbound_codecs: e.target.value })}
          disabled={loading}
          placeholder="G729,PCMU,PCMA"
          inputProps={{ style: { fontFamily: "monospace" } }}
          helperText="Order = preference (first offered to this supplier). Blank = pass the caller's codec order through. Callers locked outside this list are transcoded. Allowed: OPUS, G729, PCMU, PCMA"
        />

        {/* RT-4: number translation */}
        <TextField
          fullWidth
          margin="dense"
          label="Strip digits"
          value={formData.strip_digits}
          onChange={(e) => setFormData({ ...formData, strip_digits: e.target.value })}
          disabled={loading}
          placeholder="0"
          helperText="Digits stripped from the front of the dialed number before sending to this carrier"
        />
        <TextField
          fullWidth
          margin="dense"
          label="Prepend prefix"
          value={formData.prepend_prefix}
          onChange={(e) => setFormData({ ...formData, prepend_prefix: e.target.value })}
          disabled={loading}
          placeholder="e.g. 011"
          helperText="Prefix added to the dialed number for this carrier"
        />
        <TextField
          fullWidth
          margin="dense"
          label="ANI rewrite (optional)"
          value={formData.ani_rewrite}
          onChange={(e) => setFormData({ ...formData, ani_rewrite: e.target.value })}
          disabled={loading}
          placeholder="e.g. 12125550100"
          helperText="Fixed caller ID presented on this leg (empty = pass through)"
        />

        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.enabled.toString()}
            label="Status"
            onChange={(e) => setFormData({ ...formData, enabled: e.target.value === "true" })}
            disabled={loading}
          >
            <MenuItem value="true">Enabled</MenuItem>
            <MenuItem value="false">Disabled</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelForm} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !formData.ip_address}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Saving..." : formMode === "add" ? "Add Gateway" : "Update Gateway"}
        </Button>
      </DialogActions>
    </form>
  );

  /** Render gateway list. */
  const renderList = () => (
    <>
      <DialogContent>
        {supplierStatus === SupplierStatus.DISABLED && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This supplier is disabled. All gateways will be inactive.
          </Alert>
        )}

        {gateways.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No gateways configured. Click "Add Gateway" to create your first gateway.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Port</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Load %</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map((gateway) => (
                  <TableRow key={gateway.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <GatewayIcon fontSize="small" color="action" />
                        {gateway.ip_address}
                      </Box>
                    </TableCell>
                    <TableCell>{gateway.port}</TableCell>
                    <TableCell>{gateway.priority}</TableCell>
                    <TableCell>{gateway.load_share}%</TableCell>
                    <TableCell>
                      {gateway.capacity_cps} CPS /{" "}
                      {gateway.capacity_cc > 0
                        ? `${gateway.capacity_cc} CC`
                        : "∞ CC"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={gateway.enabled ? "Enabled" : "Disabled"}
                        size="small"
                        color={gateway.enabled ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Gateway">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(gateway)}
                          disabled={loading}
                         aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Gateway">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteGatewayRequest(gateway)}
                          disabled={loading}
                          color="error"
                         aria-label="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={loading}
        >
          Add Gateway
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GatewayIcon />
          <span>Manage Gateways</span>
        </Box>
        <Typography variant="caption" display="block" color="text.secondary">
          {supplierName}
        </Typography>
      </DialogTitle>

      {formMode === "list" ? renderList() : renderForm()}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete gateway"
        message={`Delete gateway ${pendingDelete?.ip_address ?? ""}?`}
        details="In-flight calls finish; new calls route to the remaining gateways of this supplier."
        confirmLabel="Delete"
        severity="error"
        loading={loading}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteGateway}
      />
    </Dialog>
  );
}
